// src/pages/CrewFormation.js
import React, { useState, useEffect } from 'react';
import crewImage from '../assets/images/mkd_crew.JPG';
import crewMilan from '../assets/images/crew_Milan.JPG';
import crewAmsterdam from '../assets/images/crew_Amsterdam.JPG';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useTranslation } from 'react-i18next';
import { pickL } from '../utils/i18nContent';

// ─────────────────────────────────────────────────────────────
// Formations Crew : Ados & Adultes (même programme, groupes d'âge différents)
// Tarif unique commun aux deux formations.
// hoursPerMonth : null = ligne "Volume" masquée.
// ─────────────────────────────────────────────────────────────
const PRICES = { annual: 1000, semester1: 450, semester2: 670, monthly: 120, adminFee: 25 };

const FORMATIONS = [
  { key: 'teens', hoursPerMonth: 12 },
  { key: 'adults', hoursPerMonth: null }
];

const INCLUDED_ICONS = ['✈️', '🎤', '🎬', '📸', '🏆'];

const LABELS = {
  fr: {
    annual: "Annuel",
    semester1: "Semestre 1 (sept.–déc.)",
    semester2: "Semestre 2 (jan.–juin)",
    monthly: "Mensuel (domiciliation)",
    adminFee: "Frais administratifs (annuel)",
    perMonth: "/mois",
    titles: { teens: "Formation Ados", adults: "Formation Adultes" },
    audienceLabel: "Public",
    audience: { teens: "Adolescents", adults: "Adultes" },
    rhythmLabel: "Rythme",
    rhythm: "3 à 4 dimanches par mois",
    slotLabel: "Horaire",
    slot: { teens: "En matinée", adults: "L'après-midi" },
    volumeLabel: "Volume",
    hours: (n) => `${n}h de danse par mois`,
    pricingTitle: "Tarif (identique pour les deux formations)",
    includedTitle: "Inclus dans les deux formations",
    included: ["Voyage", "Shows", "Tournage vidéo", "Shooting photo", "Concours"],
    sameProgram: "Même programme, mêmes opportunités — seul le groupe d'âge change."
  },
  nl: {
    annual: "Jaarlijks",
    semester1: "Semester 1 (sep–dec)",
    semester2: "Semester 2 (jan–jun)",
    monthly: "Maandelijks (domiciliëring)",
    adminFee: "Administratiekosten (jaarlijks)",
    perMonth: "/maand",
    titles: { teens: "Opleiding Tieners", adults: "Opleiding Volwassenen" },
    audienceLabel: "Doelgroep",
    audience: { teens: "Tieners", adults: "Volwassenen" },
    rhythmLabel: "Ritme",
    rhythm: "3 tot 4 zondagen per maand",
    slotLabel: "Tijdstip",
    slot: { teens: "In de voormiddag", adults: "In de namiddag" },
    volumeLabel: "Volume",
    hours: (n) => `${n}u dans per maand`,
    pricingTitle: "Tarief (hetzelfde voor beide opleidingen)",
    includedTitle: "Inbegrepen in beide opleidingen",
    included: ["Reis", "Shows", "Videoshoot", "Fotoshoot", "Wedstrijden"],
    sameProgram: "Hetzelfde programma, dezelfde kansen — enkel de leeftijdsgroep verschilt."
  },
  en: {
    annual: "Annual",
    semester1: "Semester 1 (Sep–Dec)",
    semester2: "Semester 2 (Jan–Jun)",
    monthly: "Monthly (direct debit)",
    adminFee: "Administrative fee (yearly)",
    perMonth: "/month",
    titles: { teens: "Teen Training", adults: "Adult Training" },
    audienceLabel: "Audience",
    audience: { teens: "Teens", adults: "Adults" },
    rhythmLabel: "Schedule",
    rhythm: "3 to 4 Sundays per month",
    slotLabel: "Time",
    slot: { teens: "Mornings", adults: "Afternoons" },
    volumeLabel: "Volume",
    hours: (n) => `${n}h of dance per month`,
    pricingTitle: "Pricing (same for both programmes)",
    includedTitle: "Included in both programmes",
    included: ["Trip", "Shows", "Video shoot", "Photo shoot", "Competitions"],
    sameProgram: "Same programme, same opportunities — only the age group changes."
  }
};

const getLabels = (lang) => {
  const l = (lang || 'fr').slice(0, 2);
  return LABELS[l] || LABELS.en;
};

const CrewFormation = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('crew');
  const L = getLabels(i18n.language);

  const [showFormData, setShowFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    experience: '',
    message: ''
  });

  const [contactFormData, setContactFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');

  const handleInscriptionClick = () => {
    navigate('/inscription-formation');
  };

  const handleShowChange = (e) => {
    const { name, value } = e.target;
    setShowFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleShowSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'quotes'), {
        ...showFormData,
        courseId: selectedCourse,
        type: 'prestation',
        createdAt: new Date(),
        status: 'new'
      });
      alert(t('page.quote.success'));
      setShowFormData({
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phone: '',
        experience: '',
        message: ''
      });
      setSelectedCourse('');
    } catch (error) {
      console.error("Error sending quote: ", error);
      alert(t('page.quote.error'));
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'contacts'), {
        ...contactFormData,
        courseId: selectedCourse,
        type: 'formation',
        createdAt: new Date(),
        status: 'new'
      });
      alert(t('page.contact.success'));
      setContactFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setSelectedCourse('');
    } catch (error) {
      console.error("Error sending message: ", error);
      alert(t('page.contact.error'));
    }
  };

  useEffect(() => {
    const fetchCourses = async () => {
      setLoadingCourses(true);
      try {
        const q = query(
          collection(db, 'dance_courses'),
          where('title', '!=', '')
        );
        const querySnapshot = await getDocs(q);
        const coursesList = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(course => {
            const frTitle = (course?.title_i18n?.fr || course?.title || '').toLowerCase();
            return frTitle.includes('formation');
          });

        setCourses(coursesList);
      } catch (err) {
        console.error("Error loading courses: ", err);
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  // Ligne de prix réutilisable
  const PriceRow = ({ label, value, highlight }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      padding: '8px 10px',
      backgroundColor: highlight ? '#efedff' : '#fff',
      border: highlight ? '1px solid #7C3AED' : 'none',
      borderRadius: '6px'
    }}>
      <span style={{ fontSize: '0.85rem', color: '#444', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: '1rem', fontWeight: 700, color: '#7C3AED' }}>{value}</span>
    </div>
  );

  // Carte d'une formation (Ados / Adultes)
  const FormationCard = ({ formation }) => {
    return (
      <div style={{
        backgroundColor: '#f9fafb',
        padding: '15px',
        borderRadius: '8px',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10px',
          right: '-10px',
          backgroundColor: '#ff9800',
          color: 'white',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          fontWeight: 'bold'
        }}>
          !
        </div>
        <h4 style={{
          color: '#7C3AED',
          marginBottom: '10px',
          textAlign: 'center',
          fontSize: '1.1rem'
        }}>
          {L.titles[formation.key]}
        </h4>

        <p style={{ margin: '6px 0', fontSize: '0.95rem' }}>
          <strong>{L.audienceLabel}:</strong> {L.audience[formation.key]}
        </p>
        <p style={{ margin: '6px 0', fontSize: '0.95rem' }}>
          <strong>{L.rhythmLabel}:</strong> {L.rhythm}
        </p>
        <p style={{ margin: '6px 0', fontSize: '0.95rem' }}>
          <strong>{L.slotLabel}:</strong> {L.slot[formation.key]}
        </p>
        {formation.hoursPerMonth ? (
          <p style={{ margin: '6px 0', fontSize: '0.95rem' }}>
            <strong>{L.volumeLabel}:</strong> {L.hours(formation.hoursPerMonth)}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="crew-formation-container">
      {/* Styles responsives dédiés à cette page */}
      <style>{styles}</style>

      <SEO
        title={t('page.seo.title')}
        description={t('page.seo.description')}
      />

      {/* Bannière Hero */}
      <div style={{
        position: 'relative',
        height: '320px',
        borderRadius: '12px',
        marginBottom: '40px',
        overflow: 'hidden',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
      }}>
        <img
          src={crewImage}
          alt={t('page.hero.alt')}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
          textShadow: '0 2px 4px rgba(0,0,0,0.5)',
          padding: '15px',
          backgroundColor: 'rgba(0,0,0,0.4)'
        }}>
          <h1 style={{
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            fontWeight: '800',
            marginBottom: '15px',
            color: '#ffffff',
            lineHeight: '1.2'
          }}>
            {t('page.hero.title')}
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.3rem)',
            fontStyle: 'italic',
            marginBottom: '12px',
            color: '#f3e8ff',
            maxWidth: '800px',
            lineHeight: '1.4'
          }}>
            {t('page.hero.subtitle')}
          </p>
        </div>
      </div>

      {/* Section Formation */}
      <div className="crew-formation-grid-2 equal" style={{ gap: '30px', marginBottom: '40px' }}>
        <div>
          <h2 style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            textAlign: 'center'
          }}>
            {t('page.overview.title')}
          </h2>

          <p style={{ lineHeight: '1.6', marginBottom: '15px', fontSize: '1rem' }}>
            <strong>{t('page.overview.p1strong')}</strong> {t('page.overview.p1')}
          </p>

          <p style={{ lineHeight: '1.6', marginBottom: '15px', fontSize: '1rem' }}>
            {t('page.overview.p2')}
          </p>

          <p style={{ lineHeight: '1.6', marginBottom: '15px', fontSize: '1rem' }}>
            {t('page.overview.p3')}
          </p>

          {/* Note sur les tarifs */}
          <div style={{
            backgroundColor: '#fff8e1',
            border: '1px solid #ffd54f',
            borderRadius: '8px',
            padding: '15px',
            marginTop: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: '#ff9800', fontSize: '1.2rem' }}>💰</span>
              <div>
                <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#7C3AED' }}>
                  {t('page.overview.financialNote.title')}
                </p>
                <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>
                  {t('page.overview.financialNote.text')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <h3 style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
            textAlign: 'center'
          }}>
            {t('page.details.title')}
          </h3>

          {/* Cartes Ados / Adultes */}
          <div className="crew-formation-grid-2-inner" style={{ marginBottom: '20px' }}>
            {FORMATIONS.map(f => (
              <FormationCard key={f.key} formation={f} />
            ))}
          </div>

          {/* Commun aux deux formations */}
          <div style={{
            backgroundColor: '#f0f9ff',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <p style={{
              fontWeight: '600',
              marginBottom: '6px',
              color: '#7C3AED',
              textAlign: 'center',
              fontSize: '1.1rem'
            }}>
              {L.includedTitle}
            </p>
            <p style={{
              margin: '0 0 12px',
              fontSize: '0.85rem',
              color: '#555',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              {L.sameProgram}
            </p>

            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '12px'
            }}>
              {L.included.map((item, i) => (
                <span key={item} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #7C3AED',
                  borderRadius: '999px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#4c1d95'
                }}>
                  <span>{INCLUDED_ICONS[i]}</span>{item}
                </span>
              ))}
            </div>

          </div>

          {/* Tarif commun */}
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <p style={{
              fontWeight: '600',
              marginBottom: '10px',
              color: '#7C3AED',
              textAlign: 'center',
              fontSize: '1.1rem'
            }}>
              {L.pricingTitle}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <PriceRow label={L.monthly} value={`${PRICES.monthly}€${L.perMonth}`} />
              <PriceRow label={L.semester1} value={`${PRICES.semester1}€`} />
              <PriceRow label={L.semester2} value={`${PRICES.semester2}€`} />
              <PriceRow label={L.annual} value={`${PRICES.annual}€`} highlight />
            </div>
            <p style={{ margin: '10px 0 0', fontSize: '0.78rem', color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
              + {PRICES.adminFee}€ ({L.adminFee})
            </p>
          </div>

          <button onClick={handleInscriptionClick} style={{
            padding: '12px 20px',
            backgroundColor: '#7C3AED',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'all 0.3s',
            marginTop: '10px',
            width: '100%'
          }}>
            {t('page.details.cta')}
          </button>
        </div>
      </div>

      {/* Section Mission et Vision */}
      <div className="crew-formation-grid-2 equal" style={{ gap: '30px', marginBottom: '40px' }}>
        <div style={{
          backgroundColor: '#f9fafb',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            marginBottom: '15px',
            position: 'relative'
          }}>
            <img
              src={crewMilan}
              alt={t('page.mission.imgAlt')}
              style={{
                borderRadius: '8px',
                width: '100%',
                height: '200px',
                objectFit: 'cover'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '4px',
              fontSize: '0.8rem'
            }}>
              {t('page.mission.caption')}
            </div>
          </div>
          <h2 style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
            textAlign: 'center'
          }}>
            {t('page.mission.title')}
          </h2>
          <p style={{ lineHeight: '1.6', fontSize: '1rem', margin: 0 }}>
            {t('page.mission.text')}
          </p>
        </div>

        <div style={{
          backgroundColor: '#f9fafb',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            marginBottom: '15px',
            position: 'relative'
          }}>
            <img
              src={crewAmsterdam}
              alt={t('page.vision.imgAlt')}
              style={{
                borderRadius: '8px',
                width: '100%',
                height: '200px',
                objectFit: 'cover'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '4px',
              fontSize: '0.8rem'
            }}>
              {t('page.vision.caption')}
            </div>
          </div>
          <h2 style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
            textAlign: 'center'
          }}>
            {t('page.vision.title')}
          </h2>
          <p style={{ lineHeight: '1.6', fontSize: '1rem', margin: 0 }}>
            {t('page.vision.text')}
          </p>
        </div>
      </div>

      {/* Section Contact */}
      <div style={{
        backgroundColor: '#f9fafb',
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '40px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      }}>
        <div>
          <h3 style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.3rem, 3vw, 2rem)',
            textAlign: 'center'
          }}>
            {t('page.contact.title')}
          </h3>
          <form onSubmit={handleContactSubmit} style={{
            display: 'grid',
            gap: '15px',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {loadingCourses ? (
              <p style={{ textAlign: 'center' }}>{t('page.contact.loading')}</p>
            ) : (
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  {t('page.contact.courseLabel')}
                </label>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">{t('page.contact.coursePlaceholder')}</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {pickL(course.title_i18n ?? course.title, i18n.language, 'fr')}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="crew-formation-grid-form">
              <div>
                <input
                  type="text"
                  name="firstName"
                  placeholder={t('page.contact.firstName')}
                  value={contactFormData.firstName}
                  onChange={handleContactChange}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <input
                  type="text"
                  name="lastName"
                  placeholder={t('page.contact.lastName')}
                  value={contactFormData.lastName}
                  onChange={handleContactChange}
                  required
                  style={inputStyle}
                />
              </div>
            </div>

            <div className="crew-formation-grid-form">
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={contactFormData.email}
                  onChange={handleContactChange}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('page.contact.phone')}
                  value={contactFormData.phone}
                  onChange={handleContactChange}
                  style={inputStyle}
                />
              </div>
            </div>

            <div>
              <textarea
                name="message"
                placeholder={t('page.contact.message')}
                value={contactFormData.message}
                onChange={handleContactChange}
                rows="4"
                required
                style={{ ...inputStyle, minHeight: '120px' }}
              ></textarea>
            </div>

            <button type="submit" style={{
              padding: '12px 20px',
              backgroundColor: '#7C3AED',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              width: '100%',
              maxWidth: '300px',
              margin: '0 auto'
            }}>
              {t('page.contact.submit')}
            </button>
          </form>
        </div>
      </div>

      {/* Section Demande de devis */}
      <div className="crew-formation-grid-2 equal" style={{
        backgroundColor: '#f9fafb',
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '40px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.3rem, 3vw, 2rem)',
            textAlign: 'center'
          }}>
            {t('page.quote.title')}
          </h2>

          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {t('page.quote.desc')}
          </p>

          <img
            src="https://thedancestudioleeds.com/wp-content/uploads/2023/10/IMG-20230311-WA0007.jpg"
            alt="Performance de danse"
            style={{
              borderRadius: '10px',
              width: '100%',
              height: '250px',
              objectFit: 'cover'
            }}
          />
        </div>

        <form onSubmit={handleShowSubmit} style={{ display: 'grid', gap: '15px' }}>
          <div className="crew-formation-grid-form">
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>{t('page.form.firstName')}</label>
              <input
                type="text"
                name="firstName"
                value={showFormData.firstName}
                onChange={handleShowChange}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>{t('page.form.lastName')}</label>
              <input
                type="text"
                name="lastName"
                value={showFormData.lastName}
                onChange={handleShowChange}
                required
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>{t('page.form.company')}</label>
            <input
              type="text"
              name="company"
              value={showFormData.company}
              onChange={handleShowChange}
              style={inputStyle}
            />
          </div>

          <div className="crew-formation-grid-form">
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>{t('page.form.email')}</label>
              <input
                type="email"
                name="email"
                value={showFormData.email}
                onChange={handleShowChange}
                required
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>{t('page.form.phone')}</label>
              <input
                type="tel"
                name="phone"
                value={showFormData.phone}
                onChange={handleShowChange}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              {t('page.form.experience')}
            </label>
            <textarea
              name="experience"
              value={showFormData.experience}
              onChange={handleShowChange}
              rows="3"
              style={{ ...inputStyle, minHeight: '100px' }}
            ></textarea>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              {t('page.form.eventDetails')}
            </label>
            <textarea
              name="message"
              value={showFormData.message}
              onChange={handleShowChange}
              rows="5"
              required
              style={{ ...inputStyle, minHeight: '150px' }}
              placeholder={t('page.form.eventPlaceholder')}
            ></textarea>
          </div>

          <button type="submit" style={{
            padding: '12px 20px',
            backgroundColor: '#7C3AED',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'all 0.3s',
            marginTop: '10px',
            width: '100%'
          }}>
            {t('page.quote.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

// Style commun pour les inputs
const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '1rem',
  backgroundColor: 'white',
  transition: 'border 0.3s',
  boxSizing: 'border-box'
};

// Media queries pour les écrans plus larges
const styles = `
  .crew-formation-container { max-width: 1100px; margin: 0 auto; padding: 20px; width: 100%; box-sizing: border-box; }
  .crew-formation-grid-2 { display: grid; grid-template-columns: 1fr; gap: 30px; }
  .crew-formation-grid-2.equal { grid-template-columns: 1fr; }
  .crew-formation-grid-2-inner { display: grid; grid-template-columns: 1fr; gap: 10px; }
  .crew-formation-grid-form { display: grid; grid-template-columns: 1fr; gap: 15px; }

  @media (min-width: 768px) {
    .crew-formation-grid-2 { grid-template-columns: 1.2fr 0.8fr; }
    .crew-formation-grid-2.equal { grid-template-columns: 1fr 1fr; }
    .crew-formation-grid-2-inner { grid-template-columns: 1fr 1fr; }
    .crew-formation-grid-form { grid-template-columns: 1fr 1fr; gap: 20px; }
  }

  @media (min-width: 1024px) {
    .crew-formation-container { padding: 30px 20px; }
  }
`;

export default CrewFormation;